import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billingMasterApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { clsx } from 'clsx';
import {
  Plus, Pencil, Trash2, X, Save, Loader2, CheckCircle2,
  Stethoscope, BedDouble, FlaskConical, Scan, Pill, Wrench, MoreHorizontal,
  IndianRupee, AlertCircle, BookOpen,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────

type Category = 'CONSULTATION' | 'BED' | 'PATHOLOGY' | 'RADIOLOGY' | 'PROCEDURE' | 'PHARMACY' | 'OTHER';

interface BillingItem {
  id:             string;
  service_name:   string;
  category:       Category;
  price:          number;
  tax_pct:        number;
  is_active:      number;
  doctor_id:      string | null;
  doctor_name:    string | null;
  specialization: string | null;
}

interface CatalogueItem {
  name:        string;
  group_label: string;
}

// ── Tab config ─────────────────────────────────────────────────────

const TABS: { key: Category; label: string; icon: React.ElementType; roles: string[] }[] = [
  { key: 'CONSULTATION', label: 'Consultation', icon: Stethoscope,    roles: ['hospital_admin','owner'] },
  { key: 'BED',          label: 'Beds',         icon: BedDouble,      roles: ['hospital_admin','owner'] },
  { key: 'PATHOLOGY',    label: 'Pathology',    icon: FlaskConical,   roles: ['hospital_admin','owner','lab_technician'] },
  { key: 'RADIOLOGY',    label: 'Radiology',    icon: Scan,           roles: ['hospital_admin','owner','radiologist'] },
  { key: 'PROCEDURE',    label: 'Procedures',   icon: Wrench,         roles: ['hospital_admin','owner'] },
  { key: 'PHARMACY',     label: 'Pharmacy',     icon: Pill,           roles: ['hospital_admin','owner','pharmacist'] },
  { key: 'OTHER',        label: 'Other',        icon: MoreHorizontal, roles: ['hospital_admin','owner'] },
];

// Categories that have a pre-built catalogue to import from
const CATALOGUE_TYPE: Partial<Record<Category, 'pathology' | 'bed' | 'radiology'>> = {
  PATHOLOGY: 'pathology',
  BED:       'bed',
  RADIOLOGY: 'radiology',
};

const FULL_ACCESS = ['hospital_admin', 'owner'];

// ── Helpers ────────────────────────────────────────────────────────

function effectivePrice(price: number, taxPct: number) {
  return (price * (1 + taxPct / 100)).toFixed(2);
}

function inr(n: number) {
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function groupLabel(key: string) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// ── Entry Modal ────────────────────────────────────────────────────

interface ModalProps {
  item?:     BillingItem;
  category:  Category;
  doctors:   { id: string; name: string; specialization: string | null }[];
  canDelete: boolean;
  onClose:   () => void;
  onSaved:   () => void;
  onDeleted: () => void;
}

function EntryModal({ item, category, doctors, canDelete, onClose, onSaved, onDeleted }: ModalProps) {
  const isEdit = !!item;

  const [form, setForm] = useState({
    service_name: item?.service_name ?? '',
    price:        String(item?.price ?? ''),
    tax_pct:      String(item?.tax_pct ?? '0'),
    is_active:    item ? Boolean(item.is_active) : true,
    doctor_id:    item?.doctor_id ?? '',
  });
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [error,      setError]      = useState('');
  const [confirmDel, setConfirmDel] = useState(false);

  function f(key: keyof typeof form, val: any) {
    setForm(s => ({ ...s, [key]: val }));
  }

  const effective = useMemo(() => {
    const p = parseFloat(form.price)   || 0;
    const t = parseFloat(form.tax_pct) || 0;
    return (p * (1 + t / 100)).toFixed(2);
  }, [form.price, form.tax_pct]);

  async function handleSave() {
    if (!form.service_name.trim()) { setError('Service name is required.'); return; }
    if (!form.price || isNaN(parseFloat(form.price))) { setError('Enter a valid price.'); return; }
    if (category === 'CONSULTATION' && !form.doctor_id) { setError('Select a doctor for consultation fees.'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        service_name: form.service_name.trim(),
        category,
        price:        parseFloat(form.price),
        tax_pct:      parseFloat(form.tax_pct) || 0,
        is_active:    form.is_active,
        doctor_id:    category === 'CONSULTATION' ? form.doctor_id || null : null,
      };
      if (isEdit) {
        await billingMasterApi.update(item!.id, payload);
      } else {
        await billingMasterApi.create(payload);
      }
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.response?.data?.error ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await billingMasterApi.remove(item!.id);
      onDeleted();
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Delete failed');
      setDeleting(false);
      setConfirmDel(false);
    }
  }

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/40';
  const lbl = 'block text-xs font-semibold text-gray-500 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-navy text-sm">
              {isEdit ? 'Edit Charge' : 'Add New Charge'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {category.charAt(0) + category.slice(1).toLowerCase()} · {isEdit ? 'Update existing' : 'New entry'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={16}/>
          </button>
        </div>

        <div className="p-6 space-y-4">

          {category === 'CONSULTATION' && (
            <div>
              <label className={lbl}>Doctor *</label>
              <select value={form.doctor_id} onChange={e => f('doctor_id', e.target.value)} className={inp}>
                <option value="">Select doctor…</option>
                {doctors.map(d => (
                  <option key={d.id} value={d.id}>
                    Dr. {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className={lbl}>Service / Charge Name *</label>
            <input
              value={form.service_name}
              onChange={e => f('service_name', e.target.value)}
              placeholder={
                category === 'CONSULTATION' ? 'e.g. OPD Consultation' :
                category === 'BED'          ? 'e.g. General Ward Bed' :
                category === 'PATHOLOGY'    ? 'e.g. Complete Blood Count (CBC)' :
                category === 'RADIOLOGY'    ? 'e.g. Chest X-Ray PA View' :
                'e.g. Dressing, Suturing'
              }
              className={inp}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Base Price (₹) *</label>
              <div className="relative">
                <IndianRupee size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input
                  type="number" min="0" step="0.01"
                  value={form.price}
                  onChange={e => f('price', e.target.value)}
                  placeholder="0.00"
                  className={clsx(inp, 'pl-7')}
                />
              </div>
            </div>
            <div>
              <label className={lbl}>GST / Tax (%)</label>
              <input
                type="number" min="0" max="100" step="0.01"
                value={form.tax_pct}
                onChange={e => f('tax_pct', e.target.value)}
                placeholder="0"
                className={inp}
              />
            </div>
          </div>

          {parseFloat(form.price) > 0 && (
            <div className="bg-orange/8 border border-orange/20 rounded-lg px-4 py-2.5 flex items-center justify-between">
              <span className="text-xs text-gray-500">Effective price (incl. tax)</span>
              <span className="text-sm font-bold text-navy">₹{effective}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-gray-500">Active</div>
              <div className="text-xs text-gray-400">Inactive items won't appear in invoices</div>
            </div>
            <button
              type="button"
              onClick={() => f('is_active', !form.is_active)}
              className={clsx(
                'relative w-10 h-5 rounded-full transition-colors',
                form.is_active ? 'bg-orange' : 'bg-gray-300',
              )}>
              <span className={clsx(
                'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                form.is_active ? 'translate-x-5' : 'translate-x-0.5',
              )}/>
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle size={12} className="flex-shrink-0"/>
              {error}
            </div>
          )}
        </div>

        <div className={clsx(
          'flex items-center px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl',
          isEdit && canDelete ? 'justify-between' : 'justify-end gap-3',
        )}>
          {isEdit && canDelete && !confirmDel && (
            <button
              onClick={() => setConfirmDel(true)}
              className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-600 transition">
              <Trash2 size={12}/> Delete
            </button>
          )}
          {isEdit && canDelete && confirmDel && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-500">Confirm delete?</span>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="text-xs font-semibold text-white bg-red-500 px-3 py-1 rounded-lg hover:bg-red-600 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
              <button onClick={() => setConfirmDel(false)} className="text-xs text-gray-400 hover:text-gray-600">
                Cancel
              </button>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white transition">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2 bg-navy text-white text-sm font-semibold rounded-xl hover:bg-navy/90 disabled:opacity-50 transition">
              {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}
              {isEdit ? 'Update' : 'Add Charge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Catalogue Import Modal ─────────────────────────────────────────

function CatalogueModal({
  category,
  existingItems,
  onClose,
  onImported,
}: {
  category:      Category;
  existingItems: BillingItem[];
  onClose:       () => void;
  onImported:    () => void;
}) {
  const catalogueType = CATALOGUE_TYPE[category]!;

  const { data: catalogue = [], isLoading } = useQuery<CatalogueItem[]>({
    queryKey: ['billing-master-catalogue', catalogueType],
    queryFn:  () => billingMasterApi.catalogue(catalogueType).then(r => r.data),
    staleTime: 10 * 60_000,
  });

  const existingNames = useMemo(
    () => new Set(existingItems.map(i => i.service_name)),
    [existingItems],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogueItem[]>();
    for (const item of catalogue) {
      if (!map.has(item.group_label)) map.set(item.group_label, []);
      map.get(item.group_label)!.push(item);
    }
    return map;
  }, [catalogue]);

  const newItems = useMemo(
    () => catalogue.filter(i => !existingNames.has(i.name)),
    [catalogue, existingNames],
  );

  const [selected,     setSelected]     = useState<Set<string>>(new Set());
  const [defaultPrice, setDefaultPrice] = useState('');
  const [importing,    setImporting]    = useState(false);
  const [error,        setError]        = useState('');
  const [result,       setResult]       = useState<{ created: number; skipped: number } | null>(null);

  function toggleItem(name: string) {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  function selectAllNew() {
    setSelected(new Set(newItems.map(i => i.name)));
  }

  async function handleImport() {
    if (!selected.size) return;
    setImporting(true);
    setError('');
    try {
      const price = parseFloat(defaultPrice) || 0;
      const res = await billingMasterApi.batchCreate({
        items: [...selected].map(name => ({
          service_name: name,
          category,
          price,
          tax_pct:   0,
          is_active: true,
        })),
      });
      setResult(res.data);
      onImported();
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-navy text-sm">Import from Catalogue</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {category.charAt(0) + category.slice(1).toLowerCase()} ·
              Select items to add — set prices after import
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={16}/>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 flex-shrink-0 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <button
              onClick={selectAllNew}
              disabled={!newItems.length}
              className="text-xs text-navy font-medium hover:underline disabled:text-gray-300 disabled:no-underline">
              Select all new ({newItems.length})
            </button>
            {selected.size > 0 && (
              <>
                <span className="text-gray-300">·</span>
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-gray-400 hover:underline">
                  Clear
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Default price:</span>
            <div className="relative">
              <IndianRupee size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input
                type="number" min="0" step="1"
                value={defaultPrice}
                onChange={e => setDefaultPrice(e.target.value)}
                placeholder="0"
                className="w-24 pl-5 pr-2 py-1 border border-gray-200 rounded-md text-xs
                           focus:outline-none focus:ring-1 focus:ring-navy/20"
              />
            </div>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-gray-300"/>
            </div>
          ) : (
            Array.from(grouped.entries()).map(([group, items]) => (
              <div key={group} className="mb-4">
                <div className="text-2xs font-semibold text-gray-400 uppercase tracking-wider px-2 py-1.5">
                  {groupLabel(group)}
                </div>
                <div className="space-y-0.5">
                  {items.map(item => {
                    const exists  = existingNames.has(item.name);
                    const checked = selected.has(item.name);
                    return (
                      <label
                        key={item.name}
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors select-none',
                          exists
                            ? 'opacity-40 cursor-not-allowed'
                            : 'cursor-pointer hover:bg-navy/4',
                          checked && !exists && 'bg-orange/5',
                        )}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={exists}
                          onChange={() => !exists && toggleItem(item.name)}
                          className="accent-orange flex-shrink-0"
                        />
                        <span className="flex-1 text-sm text-navy">{item.name}</span>
                        {exists && (
                          <span className="text-2xs font-medium text-green-700 bg-green-50
                                          px-1.5 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                            <CheckCircle2 size={10}/> Added
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {error && (
          <div className="px-6 pb-2 flex-shrink-0">
            <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle size={12} className="flex-shrink-0"/>{error}
            </div>
          </div>
        )}

        {result && (
          <div className="px-6 pb-2 flex-shrink-0">
            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
              <CheckCircle2 size={12} className="flex-shrink-0"/>
              {result.created} item{result.created !== 1 ? 's' : ''} added
              {result.skipped > 0 ? `, ${result.skipped} skipped (already exist)` : ''}.
              Set prices by clicking each row.
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <span className="text-xs text-gray-500">
            {selected.size > 0
              ? `${selected.size} item${selected.size !== 1 ? 's' : ''} selected`
              : 'None selected'}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white transition">
              {result ? 'Done' : 'Cancel'}
            </button>
            {!result && (
              <button
                onClick={handleImport}
                disabled={!selected.size || importing}
                className="flex items-center gap-1.5 px-5 py-2 bg-navy text-white text-sm
                           font-semibold rounded-xl hover:bg-navy/90 disabled:opacity-50 transition">
                {importing
                  ? <Loader2 size={13} className="animate-spin"/>
                  : <Plus size={13}/>}
                Import{selected.size > 0 ? ` (${selected.size})` : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Charges Table ──────────────────────────────────────────────────

function ChargesTable({
  items, canWrite, canDelete, onEdit,
}: {
  items:     BillingItem[];
  canWrite:  boolean;
  canDelete: boolean;
  onEdit:    (item: BillingItem) => void;
}) {
  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
        <IndianRupee size={32} strokeWidth={1.2} className="text-gray-300"/>
        <p className="text-sm">No charges configured yet.</p>
        {canWrite && <p className="text-xs">Click "Add Charge" or import from catalogue to get started.</p>}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            {['Service / Charge', 'Doctor', 'Base Price', 'Tax %', 'Effective Price', 'Status', ''].map(h => (
              <th key={h} className="text-left text-xs font-semibold text-gray-400 uppercase
                                     tracking-wide px-4 py-3 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
              <td className="px-4 py-3.5 font-medium text-navy">{item.service_name}</td>
              <td className="px-4 py-3.5 text-gray-500 text-xs">
                {item.doctor_name ? `Dr. ${item.doctor_name}` : '—'}
              </td>
              <td className="px-4 py-3.5 text-gray-700 font-mono">{inr(item.price)}</td>
              <td className="px-4 py-3.5 text-gray-500">{item.tax_pct > 0 ? `${item.tax_pct}%` : '—'}</td>
              <td className="px-4 py-3.5 font-semibold text-navy">
                {inr(Number(effectivePrice(item.price, item.tax_pct)))}
              </td>
              <td className="px-4 py-3.5">
                <span className={clsx(
                  'inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full',
                  item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400',
                )}>
                  {item.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-4 py-3.5">
                {canWrite && (
                  <button
                    onClick={() => onEdit(item)}
                    className="p-1.5 text-gray-400 hover:text-navy hover:bg-navy/5 rounded-lg transition"
                    title="Edit">
                    <Pencil size={13}/>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────

export default function BillingMasterPage() {
  const queryClient = useQueryClient();
  const user        = useAuthStore(s => s.user);
  const role        = user?.role ?? '';

  const visibleTabs = TABS.filter(t => t.roles.includes(role));
  const [activeTab,       setActiveTab]       = useState<Category>(visibleTabs[0]?.key ?? 'CONSULTATION');
  const [modal,           setModal]           = useState<{ item?: BillingItem } | null>(null);
  const [catalogueOpen,   setCatalogueOpen]   = useState(false);

  const canWrite  = ['hospital_admin','owner','lab_technician','radiologist','pharmacist'].includes(role);
  const canDelete = FULL_ACCESS.includes(role);
  const hasCatalogue = !!CATALOGUE_TYPE[activeTab] && canWrite;

  const { data: items = [], isLoading } = useQuery<BillingItem[]>({
    queryKey: ['billing-master', activeTab],
    queryFn:  () => billingMasterApi.list({ category: activeTab }).then(r => r.data),
    staleTime: 60_000,
  });

  const { data: doctors = [] } = useQuery({
    queryKey: ['billing-master-doctors'],
    queryFn:  () => billingMasterApi.doctors().then(r => r.data),
    staleTime: 5 * 60_000,
    enabled:  activeTab === 'CONSULTATION',
  });

  function handleSaved() {
    setModal(null);
    queryClient.invalidateQueries({ queryKey: ['billing-master', activeTab] });
  }

  function handleDeleted() {
    setModal(null);
    queryClient.invalidateQueries({ queryKey: ['billing-master', activeTab] });
  }

  function handleImported() {
    queryClient.invalidateQueries({ queryKey: ['billing-master', activeTab] });
  }

  if (!visibleTabs.length) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        You don't have access to billing master settings.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-lg font-bold text-navy">Billing Master</h1>
        <p className="text-xs text-gray-400 mt-0.5">
          Configure service charges and pricing for invoicing.
          {!canDelete && (
            <span className="ml-1 text-orange font-medium">
              · You can manage {visibleTabs.map(t => t.label).join(' & ')} charges only.
            </span>
          )}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Tab bar */}
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {visibleTabs.map(tab => {
            const Icon   = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px',
                  active
                    ? 'border-orange text-navy'
                    : 'border-transparent text-gray-400 hover:text-navy hover:border-gray-200',
                )}>
                <Icon size={14} className={active ? 'text-orange' : ''}/>
                {tab.label}
                {!isLoading && activeTab === tab.key && items.length > 0 && (
                  <span className={clsx(
                    'text-xs px-1.5 py-0.5 rounded-full font-semibold',
                    active ? 'bg-orange/15 text-orange' : 'bg-gray-100 text-gray-400',
                  )}>
                    {items.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <p className="text-xs text-gray-500">
            {isLoading ? 'Loading…' : `${items.length} charge${items.length !== 1 ? 's' : ''} configured`}
          </p>
          {canWrite && (
            <div className="flex items-center gap-2">
              {hasCatalogue && (
                <button
                  onClick={() => setCatalogueOpen(true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 border border-navy/20 text-navy
                             text-xs font-semibold rounded-lg hover:bg-navy/5 transition">
                  <BookOpen size={12}/>
                  Import from Catalogue
                </button>
              )}
              <button
                onClick={() => setModal({})}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-navy text-white text-xs
                           font-semibold rounded-lg hover:bg-navy/90 transition shadow-sm">
                <Plus size={12}/>
                Add Charge
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-gray-300"/>
          </div>
        ) : (
          <ChargesTable
            items={items}
            canWrite={canWrite}
            canDelete={canDelete}
            onEdit={item => setModal({ item })}
          />
        )}
      </div>

      {/* Entry Modal */}
      {modal !== null && (
        <EntryModal
          item={modal.item}
          category={activeTab}
          doctors={doctors as any[]}
          canDelete={canDelete}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}

      {/* Catalogue Import Modal */}
      {catalogueOpen && CATALOGUE_TYPE[activeTab] && (
        <CatalogueModal
          category={activeTab}
          existingItems={items}
          onClose={() => setCatalogueOpen(false)}
          onImported={() => { handleImported(); }}
        />
      )}
    </div>
  );
}
